import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";

import GoogleSignUp from "shared/components/Auth/GoogleSignUp";
import FirstStep from "shared/components/Auth/RegistrationFormRedesign/FirstStep";
import SecondStep from "shared/components/Auth/RegistrationFormRedesign/SecondStep";
import {
  ApiError,
  DataProps,
} from "shared/components/Auth/RegistrationFormRedesign/types";
import SpinLoader, {
  LOADER_COLOR_DARK,
  LOADER_SIZE_LARGE,
  POSITION_ABSOLUTE,
} from "shared/components/SpinLoader";

import {
  ERROR_STATUS_NOT_CONFIRM,
  ERROR_STATUS_UNPROCESSABLE,
  GENDER_MALE,
} from "shared/constants";
import { errorEventsBirthday } from "shared/constants/analytics";
import {
  FIRST_STEP_REGISTER_FORM,
  getErrorEventEmail,
  getErrorEventPassword,
  getErrorEventsName,
  SECOND_STEP_REGISTER_FORM,
} from "shared/constants/auth";
import { WOMAN_OPEN_TO_MEN } from "shared/constants/profile";

import { registrationFailedEvent, simplyClick } from "shared/helpers/analytics";
import {
  afterRegistrationSuccess,
  prepareRegistrationData,
} from "shared/helpers/auth";
import { setCookie } from "shared/helpers/cookie";
import { getBirthdayInFormat } from "shared/helpers/date";
import {
  isValidGender,
  validateAge,
  validateEmail,
  validateName,
  validatePassword,
} from "shared/helpers/validation";

import { registrationUser } from "shared/services/auth";

type Props = {
  isModal: boolean;
};

const RegistrationFormRedesign: React.FC<Props> = ({ isModal }) => {
  const [step, setStep] = useState<number>(FIRST_STEP_REGISTER_FORM);
  const [loader, setLoader] = useState<boolean>(false);
  const [data, setData] = useState<DataProps>({
    gender: GENDER_MALE,
    lookingGender: [WOMAN_OPEN_TO_MEN],
    first_name: "",
    email: "",
    password: "",
  });
  const [errorGender, setErrorGender] = useState<{
    gender: string;
    lookingGender: string;
  }>({
    gender: "",
    lookingGender: "",
  });

  const [errorDate, setErrorDate] = useState<string>("");
  const [errorDay, setErrorDay] = useState<boolean>(false);
  const [errorMonth, setErrorMonth] = useState<boolean>(false);
  const [errorYear, setErrorYear] = useState<boolean>(false);
  const [errorName, setErrorName] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [termsError, setTermsError] = useState<string>("");
  const [checkBox, setCheckBox] = useState<boolean>(false);
  const [responseError, setResponseError] = useState<string>("");

  const { t } = useTranslation("auth");

  useEffect(() => {
    const formStaticBlock = document.querySelector(".register__static");
    const heroStaticBlock = document.querySelector(".hero");

    if (!formStaticBlock || !heroStaticBlock) {
      return;
    }

    if (step === SECOND_STEP_REGISTER_FORM) {
      formStaticBlock.classList.add("second__step");
      heroStaticBlock.classList.add("second__step");
    }
  }, [step]);

  const handleClickContinue = () => {
    const resultGender = isValidGender(data.gender);
    let isSuccessValidation = true;

    if (!resultGender) {
      setErrorGender((prev) => ({ ...prev, gender: t("genderError") }));
      return;
    }

    if (data.lookingGender?.length === 0) {
      setErrorGender((prev) => ({
        ...prev,
        lookingGender: t("lookingGenderError"),
      }));
      return;
    }

    if (step === FIRST_STEP_REGISTER_FORM && resultGender) {
      simplyClick("cl_home_p_sign_up_1_next");
      setStep(SECOND_STEP_REGISTER_FORM);
      return;
    }

    simplyClick("cl_home_p_sign_up_2_sign_up");

    // === 1. Validation date of birth ===
    const resultAge = validateAge(
      Number(data.month),
      Number(data.day),
      Number(data.year)
    );

    const dateKey = [
      Number(data.month) ? "M" : "",
      Number(data.day) ? "D" : "",
      Number(data.year) ? "Y" : "",
    ].join("");

    if (!Number(data.day) && !Number(data.month) && !Number(data.year)) {
      simplyClick("err_lnd_sign_up_age_nodate");
      setErrorDate(resultAge.error || "");
      isSuccessValidation = false;
    } else if (errorEventsBirthday[dateKey]) {
      simplyClick(errorEventsBirthday[dateKey]);
      setErrorDate(resultAge.error || "");
      isSuccessValidation = false;
    } else if (!resultAge.status) {
      setErrorDate(resultAge.error || "");
      isSuccessValidation = false;
    }

    if (!Number(data.month)) {
      setErrorMonth(true);
    }

    if (!Number(data.day)) {
      setErrorDay(true);
    }

    if (!Number(data.year)) {
      setErrorYear(true);
    }

    // === 2. Validation userName ===
    const { error: nameError, status: nameStatus } = validateName(
      data?.first_name ?? ""
    );

    if (!nameStatus) {
      const nameErrorType = nameError.type || "";
      const eventName = getErrorEventsName(nameErrorType, "sign_up", "lnd");

      if (eventName) {
        simplyClick(eventName);
      }
      setErrorName(nameError.text || "");
      isSuccessValidation = false;
    }

    // === 3. Validation userEmail ===
    const resultEmail = validateEmail(data.email);

    if (!resultEmail.status) {
      const resultEmailError = resultEmail.error || "";
      simplyClick(getErrorEventEmail(resultEmailError, "sign_up", "lnd"));
      setEmailError(resultEmail.error || "");
      isSuccessValidation = false;
    }

    // === 4. Validation userPassword ===
    const resultPassword = validatePassword(data.password, null, true);

    if (!resultPassword.status) {
      setPasswordError(resultPassword.errorPassword.text || "");
      isSuccessValidation = false;
    }

    if (resultPassword.errorPassword.type) {
      const resultPasswordErrorType = resultPassword.errorPassword.type || "";
      simplyClick(
        getErrorEventPassword(resultPasswordErrorType, "sign_up", "lnd")
      );
    }

    // === 5. Validation terms checkbox ===
    if (!checkBox) {
      simplyClick("err_lnd_sign_up_checkbox_nocheckbox");
      setTermsError(t("redesignRegisterForm.termsErrorText"));
      isSuccessValidation = false;
    }

    // If validation success
    if (isSuccessValidation) {
      setLoader(true);

      if (data.year && data.month && data.day) {
        const updatedData = {
          ...data,
          birthday: getBirthdayInFormat(data.year, data.month, data.day),
        };
        setData(updatedData);
        registrationUser(prepareRegistrationData(updatedData))
          .then((resp) => responseCb(resp))
          .catch((err) => responseErrorCb(err));
      }
    }

    // If there were errors
    if (!isSuccessValidation) {
      registrationFailedEvent();
    }
  };

  // Success request on api
  const responseCb = (response: object) => {
    afterRegistrationSuccess(
      response,
      false,
      () => {
        setData((prev) => ({
          ...prev,
          gender: GENDER_MALE,
        }));
        setCookie("site_consent", true);
      },
      () => setLoader(false)
    );
  };

  // Response failed from api
  const responseErrorCb = (responseErr: ApiError) => {
    setLoader(false);
    registrationFailedEvent();

    const status = responseErr.response.status;

    if (status === ERROR_STATUS_UNPROCESSABLE) {
      const { data } = responseErr.response;
      let errorText = "";

      data.errors.forEach((error) => {
        switch (error.name) {
          case "email":
            setEmailError(error.message);
            setStep(SECOND_STEP_REGISTER_FORM);
            simplyClick("err_sign_up_lnd_email_againemail");
            break;
          case "first_name":
            setStep(SECOND_STEP_REGISTER_FORM);
            setErrorName(error.message);
            break;
          case "gender":
            setStep(FIRST_STEP_REGISTER_FORM);
            setErrorGender((prev) => ({ ...prev, gender: error.message }));
            break;
          case "birthday":
            setStep(SECOND_STEP_REGISTER_FORM);
            setErrorDate(error.message);
            break;
          case "password":
            setStep(SECOND_STEP_REGISTER_FORM);
            setPasswordError(error.message);
            break;
          default:
            errorText += errorText ? " " + error.message : error.message;
        }
      });

      if (errorText) {
        setResponseError(errorText);
      }
      return;
    }

    if (status === ERROR_STATUS_NOT_CONFIRM) {
      window.location.href = "/moderation/";
      return;
    }

    setStep(SECOND_STEP_REGISTER_FORM);
    setResponseError(t("cantRegisterProfileError"));
  };

  return (
    <>
      {isModal && <div className="background__modal"></div>}
      <div className="wrapper__redesign">
        {loader && (
          <SpinLoader
            color={LOADER_COLOR_DARK}
            size={LOADER_SIZE_LARGE}
            position={POSITION_ABSOLUTE}
            withBlur={true}
          />
        )}
        {step === FIRST_STEP_REGISTER_FORM ? (
          <FirstStep
            data={data}
            setData={setData}
            errorGender={errorGender}
            isModal={isModal}
            setErrorGender={setErrorGender}
          />
        ) : (
          <SecondStep
            errorDate={errorDate}
            setErrorDate={setErrorDate}
            errorDay={errorDay}
            setErrorDay={setErrorDay}
            errorMonth={errorMonth}
            setErrorMonth={setErrorMonth}
            errorYear={errorYear}
            setErrorYear={setErrorYear}
            errorName={errorName}
            setErrorName={setErrorName}
            data={data}
            setData={setData}
            emailError={emailError}
            setEmailError={setEmailError}
            passwordError={passwordError}
            setPasswordError={setPasswordError}
            termsError={termsError}
            setTermsError={setTermsError}
            checkBox={checkBox}
            setCheckBox={setCheckBox}
          />
        )}

        {import.meta.env.VITE_APP_REG_ADDITIONAL_INFO ? (
          <button onClick={handleClickContinue} className="form__button">
            {step === FIRST_STEP_REGISTER_FORM ? t("tryFree") : t("withEmail")}
          </button>
        ) : (
          <button
            onClick={handleClickContinue}
            className={cn("form__button", {
              second__step: step === SECOND_STEP_REGISTER_FORM,
            })}
          >
            {step === FIRST_STEP_REGISTER_FORM
              ? t("redesignRegisterForm.buttonNextText")
              : t("redesignRegisterForm.buttonSignUpText")}
          </button>
        )}
        {responseError && <p className="error__redesign">{responseError}</p>}

        {import.meta.env.VITE_APP_GOOGLE_AUTH_CLIENT_ID &&
          step === SECOND_STEP_REGISTER_FORM && (
            <>
              <span className="line"></span>
              <GoogleSignUp
                // @ts-expect-error: It is necessary to correctly define the data type
                data={data}
                setData={setData}
                setLoader={setLoader}
                setResponseError={setResponseError}
                setEmailError={setEmailError}
                className="redesign"
                setTermsError={setTermsError}
                checkBox={checkBox}
              />
            </>
          )}
      </div>
    </>
  );
};

export default RegistrationFormRedesign;
